import { Component, inject, input, model, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AutoMaskDirective, DwInputAutoCompleteDirective, LoadingService } from '@digiwall/core';
import { CurrentCompanyStore, SharedModule } from '@digiwall/goliath-core';
import {
  InvoiceTermService,
  InvoiceTermSliceDto,
  InvoiceTermSliceType,
  PaymentTermLookupDto,
  PaymentTermService,
  VatCodeDto,
} from '@digiwall/goliath-core/proxy';
import {
  BaseDocLineDto,
  CustomerInvoiceService,
  CustomerSalesPurchaseDto,
  DepositInvoiceCreationDto,
  DocLineRole,
  CustomerSalesPurchaseService,
} from '@digiwall/goliath-documents/proxy';
import { ArticleService } from '@digiwall/goliath-products/proxy';
import { DialogBoxComponent } from '@digiwall/ui-kit';
import { combineLatest, first } from 'rxjs';

@Component({
  selector: 'document-deposit-invoice-creation',
  templateUrl: './deposit-invoice-creation.component.html',
  imports: [
    DialogBoxComponent,
    ReactiveFormsModule,
    SharedModule,
    AutoMaskDirective,
    DwInputAutoCompleteDirective,
  ],
})
export class DepositInvoiceCreationComponent implements OnInit {
  private currentCompanyStore = inject(CurrentCompanyStore);

  public salesPurchase = model<CustomerSalesPurchaseDto>();
  public languageCode = model<string>();
  public route = model<ActivatedRoute>();

  protected isLoading: boolean = true;
  protected form: FormGroup;
  protected title: string;
  protected paymentTerms: PaymentTermLookupDto[];
  protected invoiceTermSlice: InvoiceTermSliceDto;

  private loadingKey: string = `${DepositInvoiceCreationComponent.name}-loading`;
  private maxDepositAmount: number;

  constructor(
    private formBuilder: FormBuilder,
    private articleService: ArticleService,
    private paymentTermService: PaymentTermService,
    private salesPurchaseService: CustomerSalesPurchaseService,
    private invoiceService: CustomerInvoiceService,
    private invoiceTermService: InvoiceTermService,
    private loadingService: LoadingService,
    private router: Router,
  ) {
    this.loadingService.setLoading(this.loadingKey, this.isLoading);

    this.paymentTermService
      .getPaymentTermLookup()
      .subscribeOnce(paymentTerms => (this.paymentTerms = paymentTerms.items));
  }

  ngOnInit(): void {
    this.computeMaxDepositAmount();

    if (this.salesPurchase().invoiceTermId != null) {
      this.initDepositFromInvoiceTermSlice();
    } else {
      this.initDeposit();
    }
  }

  protected generateDepositInvoice(): void {
    this.form.markAllAsTouched();
    if (!this.form.valid) return;

    const requestBody = this.getDepositInvoiceRequestBody();

    this.invoiceService.createDepositInvoice(requestBody).subscribeOnce(depositInvoice => {
      if (depositInvoice) {
        this.router.navigate(['../../customer-invoices', depositInvoice.id], {
          relativeTo: this.route(),
        });
      }
    });
  }

  private computeMaxDepositAmount(): void {
    let depositAmount = 0;
    this.salesPurchase().lines.forEach(
      line => (depositAmount += this.isDeposit(line) ? (line as any).totalPrice : 0),
    );

    this.maxDepositAmount = this.salesPurchase().totalAmount + depositAmount;
  }

  private isDeposit(line: BaseDocLineDto) {
    return line.lineRole == DocLineRole.SalesPurchaseArticle && (line as any).isDeposit;
  }

  private initDepositFromInvoiceTermSlice(): void {
    this.salesPurchaseService
      .nextInvoiceTermSlice(this.salesPurchase().id)
      .subscribeOnce(nextInvoiceTermSlice => {
        this.invoiceTermSlice = nextInvoiceTermSlice;
        const result = this.calculateDepositAmount(nextInvoiceTermSlice);
        this.initDeposit(result);
      });
  }

  private calculateDepositAmount(nextInvoiceTermSlice: InvoiceTermSliceDto) {
    let valueGetter = (x: InvoiceTermSliceDto) => x.value;
    if (nextInvoiceTermSlice.inclVAT) {
      // Get the vat rate to use
      const vatRate = this.salesPurchase()
        .lines.reduce((rate, line: any) => {
          if (this.isDeposit(line)) return rate;
          return line.vatRate ?? rate;
        }, 0)
        .toFixed(4);

      valueGetter = x => {
        if (!x.inclVAT) return x.value;
        return x.extraProperties[vatRate] ?? x.value;
      };
    }

    switch (nextInvoiceTermSlice.type) {
      case InvoiceTermSliceType.Amount: {
        return valueGetter(nextInvoiceTermSlice);
      }
      case InvoiceTermSliceType.Percentage: {
        return (this.maxDepositAmount / 100) * nextInvoiceTermSlice.value;
      }
      case InvoiceTermSliceType.Balance: {
        return this.maxDepositAmount - valueGetter(nextInvoiceTermSlice);
      }
    }
  }

  private initDeposit(amount = 0): void {
    this.articleService
      .getByCode(this.currentCompanyStore.currentCompany().articlesConfiguration.depositArticleCode)
      .subscribeOnce(article => {
        this.initForm(
          article.name.allTranslations[this.languageCode()] ?? article.name.current,
          Math.min(amount, this.maxDepositAmount),
        );
      });
  }

  private initForm(description: string, amount: number): void {
    this.form = this.formBuilder.group({
      description: [description, [Validators.required]],
      amount: [
        amount,
        [Validators.required, Validators.min(1), Validators.max(this.maxDepositAmount)],
      ],
      paymentTermId: [this.salesPurchase().paymentTermId],
    });

    this.isLoading = false;
    this.loadingService.setLoading(this.loadingKey, this.isLoading);
  }

  private getDepositInvoiceRequestBody(): DepositInvoiceCreationDto {
    const rawFormValue = this.form.getRawValue();
    const requestBody = {
      salesPurchaseId: this.salesPurchase().id,
      description: rawFormValue.description,
      depositAmount: rawFormValue.amount,
      paymentTermId: rawFormValue.paymentTermId,
      invoiceTermSliceId: this.invoiceTermSlice?.id,
    } as DepositInvoiceCreationDto;
    return requestBody;
  }
}
